
import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Copy, RotateCcw, CheckCircle, Download } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface OverviewDisplayProps {
  profileOverview: string;
  onReset: () => void;
}

const OverviewDisplay: React.FC<OverviewDisplayProps> = ({ 
  profileOverview,
  onReset
}) => {
  const { toast } = useToast();
  const textRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (profileOverview) {
      navigator.clipboard.writeText(profileOverview);
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        description: "The profile overview has been copied to your clipboard.",
      });
      
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (profileOverview) {
      const element = document.createElement("a");
      const file = new Blob([profileOverview], {type: 'text/plain'});
      element.href = URL.createObjectURL(file);
      element.download = "freelance-profile-overview.txt";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      toast({
        title: "Profile downloaded",
        description: "Your profile overview has been downloaded as a text file.",
      });
    }
  };

  // Preserve formatting of emojis, line breaks and special characters
  const formattedProfile = profileOverview
    .split('\n')
    .map((line, i) => (
      <React.Fragment key={i}>
        {line}
        <br />
      </React.Fragment>
    ));

  return (
    <div className="space-y-6">
      <Card className="p-6 overflow-auto bg-background/50 shadow-inner border border-muted/30 rounded-lg">
        <div 
          ref={textRef}
          className="whitespace-pre-wrap text-base leading-relaxed custom-profile-text"
          style={{ 
            minHeight: '350px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >
          {formattedProfile}
        </div>
      </Card>

      <div className="flex justify-end gap-3 mt-4">
        <Button variant="outline" onClick={onReset} className="gap-2 px-4 py-2">
          <RotateCcw className="h-4 w-4" />
          <span>New Overview</span>
        </Button>
        
        <Button 
          onClick={handleDownload}
          variant="outline"
          className="gap-2 px-4 py-2"
        >
          <Download className="h-4 w-4" />
          <span>Download</span>
        </Button>
        
        <Button 
          onClick={handleCopy} 
          className="gap-2 px-4 py-2"
          variant={copied ? "outline" : "default"}
        >
          {copied ? (
            <>
              <CheckCircle className="h-4 w-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              <span>Copy</span>
            </>
          )}
        </Button>
      </div>
      
      <div className="text-sm text-muted-foreground mt-2 text-center">
        <p>Tip: You may need to adjust formatting when pasting on platforms like Upwork.</p>
      </div>
    </div>
  );
};

export default OverviewDisplay;
